import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Box, Button, IconButton, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ButtonDropdown from '@ui/components/ButtonDropdown';
import UsageManualPdfViewer from './UsageManualPdfViewer';
import { getDocumentForLanguage, USAGE_MANUAL_LANGUAGES } from '../constants';
import { HugeIconsExpand } from '@ui/assets/custom-icons/expand-icon';
import { HugeIconsUpload04 } from '@ui/assets/custom-icons/upload-4';
const UsageManualPreviewPanel = ({ manual, onUploadClick, onEditClick, onAddLanguageClick }) => {
    const theme = useTheme();
    const { t, i18n } = useTranslation();
    const [selectedLang, setSelectedLang] = useState(i18n.language?.split('-')[0]?.toLowerCase() || 'en');
    useEffect(() => {
        setSelectedLang(i18n.language?.split('-')[0]?.toLowerCase() || 'en');
    }, [i18n.language]);
    const selectedDocument = useMemo(() => getDocumentForLanguage(manual, selectedLang), [manual, selectedLang]);
    const flagIcon = (src) => (_jsx("img", { src: src, alt: '', style: { width: 20, height: 20, objectFit: 'contain', display: 'block' } }));
    const languageOptions = USAGE_MANUAL_LANGUAGES.map((language) => ({
        id: language.id,
        text: t(`language.${language.id === 'en' ? 'english' : language.id === 'nl' ? 'dutch' : 'german'}`),
        icon: flagIcon(language.flagUrl ?? ''),
        group: true
    }));
    const selectedLanguage = USAGE_MANUAL_LANGUAGES.find((l) => l.id === selectedLang);
    const getLanguageLabel = (languageCode) => {
        const lang = USAGE_MANUAL_LANGUAGES.find((item) => item.id === languageCode);
        if (!lang)
            return languageCode.toUpperCase();
        if (lang.id === 'en')
            return t('language.english');
        if (lang.id === 'nl')
            return t('language.dutch');
        return t('language.german');
    };
    const handleDownload = () => {
        if (!selectedDocument?.documentUrl)
            return;
        const url = selectedDocument.documentUrl.includes('?')
            ? `${selectedDocument.documentUrl}&download=true`
            : `${selectedDocument.documentUrl}?download=true`;
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', selectedDocument.documentName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    const handleOpenFullscreen = () => {
        if (!selectedDocument?.documentUrl)
            return;
        window.open(selectedDocument.documentUrl, '_blank', 'noopener,noreferrer');
    };
    const handleEditClick = () => {
        if (!selectedDocument)
            return;
        onEditClick?.(selectedDocument.id, selectedLang);
    };
    const handleAddLanguageClick = () => {
        onAddLanguageClick?.(selectedLang, {
            major: manual.majorVersion,
            minor: manual.minorVersion,
            patch: manual.patchVersion
        });
    };
    return (_jsxs(Box, { sx: {
            borderRadius: 3,
            p: 2,
            bgcolor: theme.palette.common.white,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            height: '100%',
            boxShadow: theme.customShadows.card
        }, children: [_jsxs(Box, { sx: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    flexWrap: 'wrap'
                }, children: [_jsx(Typography, { variant: 'h6', children: t('help.usageManual.manualPreview') }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [onEditClick && selectedDocument && (_jsx(Button, { variant: 'outlined', color: 'inherit', size: 'medium', onClick: handleEditClick, children: t('help.usageManual.edit') })), _jsx(Button, { variant: 'outlined', color: 'inherit', size: 'medium', onClick: handleDownload, disabled: !selectedDocument, children: t('help.usageManual.downloadPdf') }), _jsx(IconButton, { size: 'medium', onClick: handleOpenFullscreen, disabled: !selectedDocument, "aria-label": 'Open fullscreen', color: 'inherit', children: _jsx(HugeIconsExpand, { color: selectedDocument ? theme.palette.text.primary : theme.palette.action.disabled }) }), _jsx(ButtonDropdown, { variant: 'outlined', size: 'medium', color: 'primary', label: '', options: languageOptions, selectedItem: selectedLang, onSelectionChange: setSelectedLang, startIcon: flagIcon(selectedLanguage?.flagUrl ?? '/assets/images/flags/us-flag.svg'), displayWithIdForLabel: true })] })] }), selectedDocument ? (_jsx(UsageManualPdfViewer, { documentUrl: selectedDocument.documentUrl, title: selectedDocument.documentName, height: 'calc(100dvh - 360px)' }, `${manual.versionLabel}-${selectedLang}`)) : (_jsxs(Box, { sx: {
                    width: '100%',
                    height: 'calc(100dvh - 360px)',
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    bgcolor: theme.palette.grey[100],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 2,
                    p: 3,
                    textAlign: 'center'
                }, children: [_jsx(Typography, { variant: 'body2', color: 'text.secondary', children: t('help.usageManual.noManualForLanguage', {
                            language: getLanguageLabel(selectedLang)
                        }) }), onAddLanguageClick && (_jsx(Button, { variant: 'outlined', color: 'inherit', size: 'medium', startIcon: _jsx(HugeIconsUpload04, { color: theme.palette.text.primary }), onClick: handleAddLanguageClick, children: t('help.usageManual.uploadForLanguage') }))] }))] }));
};
export default UsageManualPreviewPanel;
